import {
  type ConnectButton_detailsModalOptions as ThirdwebConnectDetailsModalOptions,
  darkTheme,
} from 'thirdweb/react';

import { type ThirdwebClient, createThirdwebClient } from 'thirdweb';
import { defineChain } from 'thirdweb/chains';
import { createWallet, inAppWallet } from 'thirdweb/wallets';

import {
  ACTIVE_ENV,
  BITCOIN_COM_LOGO_URL,
  ETHEREUM_CHAIN,
  SEPOLIA_ETH_CHAIN,
  SMARTBCH_CHAIN,
  THIRDWEB_CLIENT_ID,
  WALLETCONNECT_PROJECT_ID,
  WALLET_CONNECT_APP_ICON_URL,
  WALLET_CONNECT_APP_METADATA,
} from './constants';
import { RPC_CHAIN_ID, getPreferredRpcUrl } from './rpc';

const isDev = ACTIVE_ENV === 'development' || ACTIVE_ENV === 'staging';

// createThirdwebClient() throws if given an empty clientId, which would take
// down the whole SSR build. Gate on this instead so the app falls back to the
// legacy connect flow until a real GATSBY_THIRDWEB_CLIENT_ID is configured.
export const hasThirdwebClient = Boolean(THIRDWEB_CLIENT_ID);

export const client: ThirdwebClient | undefined = hasThirdwebClient
  ? createThirdwebClient({ clientId: THIRDWEB_CLIENT_ID as string })
  : undefined;

// Metadata sent to WalletConnect so mobile wallets show the correct app icon.
// thirdweb's default falls back to `origin/favicon.ico`, which is unreachable
// when testing locally. Wrapping each wallet's connect() is the only way to
// inject this into options.walletConnect before the WC controller calls
// UniversalProvider.init — the controller never reads client.appMetadata.
const WC_APP_METADATA = {
  name: WALLET_CONNECT_APP_METADATA.name,
  url: WALLET_CONNECT_APP_METADATA.url,
  description: WALLET_CONNECT_APP_METADATA.description,
  logoUrl: WALLET_CONNECT_APP_ICON_URL,
};

const ethereum = defineChain({
  id: ETHEREUM_CHAIN,
  name: 'Ethereum',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpc: getPreferredRpcUrl(ETHEREUM_CHAIN),
});

const polygon = defineChain({
  id: 137,
  name: 'Polygon',
  nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
  rpc: getPreferredRpcUrl(RPC_CHAIN_ID.POLYGON),
});

const smartBCH = defineChain({
  id: SMARTBCH_CHAIN,
  name: 'smartBCH',
  nativeCurrency: { name: 'BCH', symbol: 'BCH', decimals: 18 },
  rpc: getPreferredRpcUrl(SMARTBCH_CHAIN),
  blockExplorers: [{ name: 'SmartScout', url: 'https://www.smartscout.cash' }],
});

const avalanche = defineChain({
  id: 43114,
  name: 'Avalanche',
  nativeCurrency: { name: 'Avalanche', symbol: 'AVAX', decimals: 18 },
  rpc: getPreferredRpcUrl(RPC_CHAIN_ID.AVALANCHE),
});

const bsc = defineChain({
  id: 56,
  name: 'BNB Smart Chain',
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  rpc: getPreferredRpcUrl(RPC_CHAIN_ID.BSC),
});

const sepolia = defineChain({
  id: SEPOLIA_ETH_CHAIN,
  name: 'Sepolia',
  nativeCurrency: { name: 'Sepolia Ether', symbol: 'ETH', decimals: 18 },
  rpc: getPreferredRpcUrl(SEPOLIA_ETH_CHAIN),
});

const hoodi = defineChain({
  id: 560048,
  name: 'Hoodi',
  nativeCurrency: { name: 'Hoodi Ether', symbol: 'ETH', decimals: 18 },
  rpc: getPreferredRpcUrl(RPC_CHAIN_ID.HOODI),
});

// Same networks DAppProvider.tsx registers for @usedapp/core (minus dev-only
// testnets outside of dev/staging). defineChain accepts a single RPC URL, so
// this is the preferred/paid node; useDApp reads fail over separately.
export const chains = isDev
  ? [ethereum, polygon, smartBCH, avalanche, bsc, sepolia, hoodi]
  : [ethereum, polygon, smartBCH, avalanche, bsc];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function withWCAppMetadata(wallet: any): any {
  const orig = wallet.connect.bind(wallet);
  // eslint-disable-next-line no-param-reassign
  wallet.connect = (options: any) => {
    if (options?.walletConnect) {
      return orig({
        ...options,
        walletConnect: {
          ...options.walletConnect,
          projectId:
            options.walletConnect.projectId || WALLETCONNECT_PROJECT_ID,
          appMetadata: WC_APP_METADATA,
          optionalChains: chains,
        },
      });
    }
    return orig(options);
  };
  return wallet;
}

// Matches the wallet roster + Social Login options used in
// simple-payment-link-verse/poc-dca. Only appMetadata/projectId needed here
// are injected at connect-time by withWCAppMetadata (createWallet's
// createOptions don't accept a walletConnect key for these wallet ids).
export const wallets = [
  inAppWallet({
    auth: {
      options: [
        'google',
        'discord',
        'telegram',
        'farcaster',
        'email',
        'x',
        'passkey',
        'phone',
        'steam',
        'twitch',
        'github',
        'coinbase',
        'facebook',
        'tiktok',
        'line',
        'apple',
        'guest',
      ],
    },
  }),
  withWCAppMetadata(createWallet('io.metamask')),
  withWCAppMetadata(createWallet('com.bitcoin')),
  withWCAppMetadata(createWallet('io.rabby')),
  withWCAppMetadata(createWallet('com.coinbase.wallet')),
  withWCAppMetadata(createWallet('com.trustwallet.app')),
  withWCAppMetadata(createWallet('io.zerion.wallet')),
  withWCAppMetadata(createWallet('com.ledger')),
];

// Verse DEX is dark-navy (#0F1823, see components/Tile/styled.ts); the
// connect/details button chrome itself is themed directly in LoginButton.tsx
// to match the app's existing gradient CTA styling.
export const thirdwebTheme = darkTheme({
  colors: {
    modalBg: '#0F1823',
    borderColor: '#273953',
    separatorLine: '#273953',
    primaryButtonBg: '#2563eb',
    primaryButtonText: '#ffffff',
    accentText: '#2563eb',
  },
});

/** WalletConnect (especially mobile) may need longer than the default to restore after reload. */
export const thirdwebAutoConnect = { timeout: 60_000 } as const;

/**
 * Locks the post-connect "Select network" UI to app chains only. Without
 * this, thirdweb merges in a default catalog (Base, Arbitrum, etc).
 */
export const thirdwebConnectDetailsModal: ThirdwebConnectDetailsModalOptions = {
  connectedAccountAvatarUrl: BITCOIN_COM_LOGO_URL,
  networkSelector: {
    sections: [{ label: 'Networks', chains }],
  },
};

/** Shared connect-modal branding (matches simple-payment-link-verse). */
export const thirdwebConnectModal = {
  showThirdwebBranding: false,
  size: 'wide' as const,
  title: 'Connect Wallet',
  titleIcon: WALLET_CONNECT_APP_ICON_URL,
};

/** App metadata for thirdweb ConnectButton (shown in wallet pairing UIs). */
export const thirdwebAppMetadata = {
  name: WALLET_CONNECT_APP_METADATA.name,
  url: WALLET_CONNECT_APP_METADATA.url,
  description: WALLET_CONNECT_APP_METADATA.description,
  logoUrl: WALLET_CONNECT_APP_ICON_URL,
};

/** thirdweb `Chain` for a chain id already in `chains`; unknown ids resolve to Ethereum. */
export function getThirdwebChain(chainId: number) {
  return chains.find(chain => chain.id === chainId) ?? ethereum;
}
